import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import mcbLogo from "../images/mcb-logo.png";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const MANIFEST_API = `${API_BASE}/manifest`;
const VESSELS_API = `${API_BASE}/vessels`;

const MPI_DENSITY_TABLE = [
  [200, 0.770], [210, 0.804], [220, 0.838], [230, 0.872], [240, 0.906],
  [250, 0.940], [260, 0.974], [270, 1.008], [280, 1.042], [290, 1.076],
  [300, 1.110], [310, 1.144], [320, 1.178], [330, 1.212], [340, 1.246],
  [350, 1.280], [360, 1.314], [370, 1.348], [380, 1.382], [390, 1.416],
  [400, 1.450], [410, 1.478], [420, 1.505], [430, 1.533], [440, 1.560],
  [450, 1.588], [460, 1.615], [470, 1.643], [480, 1.670], [490, 1.698],
  [500, 1.725], [510, 1.753], [520, 1.780], [530, 1.808], [540, 1.835],
  [550, 1.863], [560, 1.890], [570, 1.918], [580, 1.945], [590, 1.973],
  [600, 2.000], [650, 2.150], [700, 3.000],
];

function getMpiMinArea(w) {
  w = Number(w || 0);
  if (w <= 0) return 0.770;
  for (let i = MPI_DENSITY_TABLE.length - 1; i >= 0; i--) {
    if (w >= MPI_DENSITY_TABLE[i][0]) return MPI_DENSITY_TABLE[i][1];
  }
  return 0.770;
}

function toTitle(v) {
  return String(v || "").replace(/_/g, " ").replace(/-/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

function fmt(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" });
}

export default function LoadPlanPrint() {
  const { id } = useParams();
  const [sailing, setSailing] = useState(null);
  const [vessel, setVessel] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const mRes = await fetch(`${MANIFEST_API}/${id}`, { cache: "no-store" });
        const mData = await mRes.json();
        setSailing(mData.sailing);
        setBookings(mData.bookings || []);
        const vid = mData.sailing?.vessel?._id || mData.sailing?.vessel;
        if (vid) {
          const vRes = await fetch(`${VESSELS_API}/${vid}`, { cache: "no-store" });
          const vData = await vRes.json();
          setVessel(vData?.data || vData);
        }
      } catch (e) {
        console.error(e);
        setError("Could not load this load plan.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div style={{ padding: 40, fontFamily: "sans-serif" }}>Loading load plan...</div>;
  if (error || !sailing) return <div style={{ padding: 40, fontFamily: "sans-serif" }}>{error || "Sailing not found."}</div>;

  const plan = sailing.loadPlan;
  const pens = plan?.pens || [];
  const penNumSort = (a, b) => {
    if (a.isContainerSlot) return 1;
    if (b.isContainerSlot) return -1;
    const na = parseInt(String(a.penNumber || "").replace(/\D/g, ""), 10) || 0;
    const nb = parseInt(String(b.penNumber || "").replace(/\D/g, ""), 10) || 0;
    return na - nb;
  };
  const portPens = pens.filter(p => p.penNumber?.startsWith("P") || vessel?.zones?.find(z => z.penNumber === p.penNumber)?.row === "port").sort(penNumSort);
  const starboardPens = pens.filter(p => p.penNumber?.startsWith("S") || vessel?.zones?.find(z => z.penNumber === p.penNumber)?.row === "starboard").sort(penNumSort);

  const totalHead = pens.reduce((s, p) => s + (p.allocations?.reduce((ss, a) => ss + a.headCount, 0) || 0), 0);

  const totalWeight = pens.reduce((s, p) => s + (p.allocations?.reduce((ss, a) => ss + a.headCount * (a.avgWeightKg || 0), 0) || 0), 0);
  const totalArea = (vessel?.zones || []).filter(z => !z.isHospitalPen && !z.isSpare && z.type !== "freight").reduce((s, z) => s + Number(z.areaM2 || 0), 0);
  const avgWeight = plan?.avgWeightKg || 450;
  const mpiMinArea = plan?.mpiMinAreaPerHead || getMpiMinArea(avgWeight);
  const mpiMaxHead = totalArea ? Math.floor(totalArea / mpiMinArea) : 0;
  const complianceOk = totalHead <= mpiMaxHead;
  const checklist = sailing.complianceChecklist || {};

  // Same clean, reliable style pattern as SailingSchedulePrint.jsx — no
  // custom SVG, no positioning math, just plain tables.
  const sectionTitleStyle = {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.09em",
    color: "#1a1a1a",
    borderBottom: "1px solid #eee",
    paddingBottom: 6,
    marginBottom: 10,
  };
  const thStyle = {
    padding: "8px 10px",
    textAlign: "left",
    fontWeight: 700,
    fontSize: 9,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    whiteSpace: "nowrap",
  };
  const tdStyle = { padding: "8px 10px" };

  return (
    <div style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", background: "#fff", color: "#1a1a1a", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 15mm 12mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        * { box-sizing: border-box; }
        body { margin: 0; }
        .print-avoid-break { break-inside: avoid; page-break-inside: avoid; }
        .print-section tr { break-inside: avoid; page-break-inside: avoid; }
      `}</style>

      {/* Screen toolbar */}
      <div className="no-print" style={{ background: "#1a1a1a", padding: "12px 32px", display: "flex", alignItems: "center", gap: 16 }}>
        <Link to={`/dashboard/sailings/${id}`} style={{ color: "#fff", fontSize: 13, textDecoration: "none", opacity: 0.7 }}>← Back to Sailing</Link>
        <button onClick={() => window.print()} style={{ marginLeft: "auto", background: "#7a1428", color: "#fff", border: "none", borderRadius: 6, padding: "8px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          Print / Save PDF
        </button>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 24px 60px" }}>

        {/* ── HEADER — matches SailingSchedulePrint.jsx exactly ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #1a1a1a", paddingBottom: 16, marginBottom: 20 }}>
          <div>
            <img src={mcbLogo} alt="McCallum Bros Ltd" style={{ height: 44, width: "auto" }} />
            <div style={{ fontSize: 11, color: "#666", marginTop: 6 }}>747 Rosebank Road, Avondale, Auckland</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>LIVESTOCK LOAD PLAN</div>
            <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>MNZ Rule Part 24c Compliant</div>
            <div style={{ fontSize: 11, color: "#666" }}>Generated: {fmt(new Date())}</div>
          </div>
        </div>

        {/* ── VOYAGE DETAILS ── */}
        <div>
          <div style={sectionTitleStyle}>Voyage Details</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginBottom: 20 }}>
            <tbody>
              <tr style={{ background: "#fafafa", borderBottom: "1px solid #eee" }}>
                <td style={{ ...tdStyle, fontWeight: 700, width: "16%" }}>Sailing Ref</td>
                <td style={tdStyle}>{sailing.sailingRef}</td>
                <td style={{ ...tdStyle, fontWeight: 700, width: "16%" }}>Vessel</td>
                <td style={tdStyle}>{sailing.vesselName || vessel?.name || "—"}</td>
              </tr>
              <tr style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ ...tdStyle, fontWeight: 700 }}>Route</td>
                <td style={tdStyle}>{sailing.origin} → {sailing.destination}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>ETD</td>
                <td style={tdStyle}>{fmt(sailing.etd)}</td>
              </tr>
              <tr style={{ background: "#fafafa", borderBottom: "1px solid #eee" }}>
                <td style={{ ...tdStyle, fontWeight: 700 }}>Status</td>
                <td style={tdStyle}>{toTitle(sailing.status)}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>Load Plan Status</td>
                <td style={tdStyle}>{toTitle(plan?.status || "none")}</td>
              </tr>
              <tr style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ ...tdStyle, fontWeight: 700 }}>Total Head</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{totalHead.toLocaleString()}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>Total Weight</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{Math.round(totalWeight).toLocaleString()} kg</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ── VESSEL PEN LAYOUT — visual guide ── */}
        <div className="print-avoid-break" style={{ marginBottom: 16 }}>
          <div style={sectionTitleStyle}>Vessel Pen Layout — {sailing.vesselName || vessel?.name || "William Fraser"}</div>
          <VesselDiagram portPens={portPens} starboardPens={starboardPens} />
        </div>

        {/* ── PEN ALLOCATION SUMMARY — the main content, a plain reliable table ── */}
        <div style={{ marginBottom: 24 }}>
          <div style={sectionTitleStyle}>Pen Allocation Summary</div>
          <table className="print-section" style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#1a1a1a", color: "#fff" }}>
                <th style={thStyle}>Pen</th>
                <th style={thStyle}>Side</th>
                <th style={thStyle}>Area (m²)</th>
                <th style={thStyle}>Customer / Consignment</th>
                <th style={thStyle}>Class</th>
                <th style={thStyle}>Head Count</th>
                <th style={thStyle}>Avg Weight</th>
                <th style={thStyle}>MPI Min Area/Head</th>
                <th style={thStyle}>MPI Max Head</th>
                <th style={thStyle}>Capacity %</th>
              </tr>
            </thead>
            <tbody>
              {[...pens].sort((a, b) => {
                const rowOf = (p) => p.penNumber?.startsWith("P") ? 0 : 1;
                const rowDiff = rowOf(a) - rowOf(b);
                return rowDiff !== 0 ? rowDiff : penNumSort(a, b);
              }).map((pen, pi) => {
                const zone = vessel?.zones?.find(z => z.penNumber === pen.penNumber || z.name === pen.penLabel);
                const side = zone?.row ? toTitle(zone.row) : pen.penNumber?.startsWith("P") ? "Port" : pen.penNumber?.startsWith("S") ? "Starboard" : "—";
                const area = zone?.areaM2 || pen.areaM2 || 0;

                if (pen.isContainerSlot) return null;

                if (pen.isHospitalPen) {
                  return (
                    <tr key={pi} style={{ background: "#fff5f5", borderBottom: "1px solid #eee" }}>
                      <td style={{ ...tdStyle, fontWeight: 700, color: "#c00" }}>{pen.penNumber}</td>
                      <td style={{ ...tdStyle, color: "#999" }}>{side}</td>
                      <td style={{ ...tdStyle, color: "#999" }}>{area || "—"}</td>
                      <td colSpan={7} style={{ ...tdStyle, color: "#c00", fontWeight: 600, fontStyle: "italic" }}>HOSPITAL PEN — Reserved for sick/injured animals (MNZ Rule Part 24c)</td>
                    </tr>
                  );
                }

                if (!pen.allocations?.length) {
                  return (
                    <tr key={pi} style={{ background: pi % 2 === 0 ? "#fafafa" : "#fff", borderBottom: "1px solid #eee" }}>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>{pen.penNumber}</td>
                      <td style={{ ...tdStyle, color: "#999" }}>{side}</td>
                      <td style={{ ...tdStyle, color: "#999" }}>{area || "—"}</td>
                      <td colSpan={7} style={{ ...tdStyle, color: "#ccc", fontStyle: "italic" }}>Empty</td>
                    </tr>
                  );
                }

                // Merge allocations that share the same customer + class
                // within this pen — e.g. two separate bookings both landing
                // in P11 as Kaingaroa/Weaner shouldn't read as two odd
                // half-rows, they're really one combined consignment as far
                // as this summary is concerned.
                const grouped = [];
                pen.allocations.forEach(alloc => {
                  const existing = grouped.find(g => g.customerName === alloc.customerName && g.animalClass === alloc.animalClass);
                  if (existing) {
                    existing.headCount += alloc.headCount;
                  } else {
                    grouped.push({ ...alloc });
                  }
                });

                // A pen shared by more than one allocation can't have each
                // row's percentage calculated against the FULL pen area
                // independently — that ignores the other allocation's real
                // space in the same pen. Show the pen's true combined area
                // usage instead when it's shared. A single-class pen
                // compares head count to head count (both whole numbers,
                // so a genuinely full pen reads as exactly 100% — MPI max
                // head is always floored, so comparing raw area instead
                // would show a full pen as ~97%, which reads as wrong even
                // though it's technically accurate).
                const isSharedPen = grouped.length > 1;
                const totalPenAreaUsed = grouped.reduce((s, a) => s + a.headCount * getMpiMinArea(a.avgWeightKg), 0);

                return grouped.map((alloc, ai) => {
                  const mpiArea = getMpiMinArea(alloc.avgWeightKg);
                  const mpiMax = area ? Math.floor(area / mpiArea) : pen.maxHead;
                  const pct = isSharedPen
                    ? (area ? Math.round((totalPenAreaUsed / area) * 100) : 0)
                    : (mpiMax > 0 ? Math.round((alloc.headCount / mpiMax) * 100) : 0);
                  const pctColor = pct >= 95 ? "#c00" : pct >= 80 ? "#c07700" : "#007a3d";
                  return (
                    <tr key={`${pi}-${ai}`} style={{ background: pi % 2 === 0 ? "#fafafa" : "#fff", borderBottom: "1px solid #eee" }}>
                      {ai === 0 && <td rowSpan={grouped.length} style={{ ...tdStyle, fontWeight: 700, verticalAlign: "top" }}>{pen.penNumber}{isSharedPen && <div style={{ fontSize: 8, color: "#c00", fontWeight: 400, fontStyle: "italic" }}>mixed pen</div>}</td>}
                      {ai === 0 && <td rowSpan={grouped.length} style={{ ...tdStyle, color: "#666", verticalAlign: "top" }}>{side}</td>}
                      {ai === 0 && <td rowSpan={grouped.length} style={{ ...tdStyle, color: "#666", verticalAlign: "top" }}>{area || "—"}</td>}
                      <td style={tdStyle}>{alloc.customerName}</td>
                      <td style={{ ...tdStyle, textTransform: "capitalize" }}>{alloc.animalClass || "—"}</td>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>{alloc.headCount}</td>
                      <td style={tdStyle}>{alloc.avgWeightKg ? `${alloc.avgWeightKg} kg` : "—"}</td>
                      <td style={tdStyle}>{mpiArea} m²</td>
                      <td style={tdStyle}>{isSharedPen ? "—" : mpiMax}</td>
                      <td style={{ ...tdStyle, fontWeight: 700, color: pctColor }}>{pct}%{isSharedPen && ai === grouped.length - 1 ? "" : ""}</td>
                    </tr>
                  );
                });
              })}
              <tr style={{ background: "#1a1a1a", color: "#fff", fontWeight: 700 }}>
                <td colSpan={5} style={{ ...tdStyle, fontSize: 11 }}>TOTAL</td>
                <td colSpan={5} style={{ ...tdStyle, fontSize: 12 }}>{totalHead}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ── LIVESTOCK CONSIGNMENT DETAIL ── */}
        <div style={{ marginBottom: 24 }}>
          <div style={sectionTitleStyle}>Livestock Consignment Detail</div>
          <table className="print-section" style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#f5f5f5" }}>
                {["Booking Ref", "Customer / Farmer", "Species", "Class", "Head Count", "Avg Weight (kg)", "Last Fed", "Last Watered", "Fit for Transport"].map(h => (
                  <th key={h} style={{ ...thStyle, borderBottom: "2px solid #ddd" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bookings.filter(b => b.bookingType === "livestock" || b.bookingType === "mixed").map((b, bi) =>
                (b.livestockItems || []).map((item, li) => (
                  <tr key={`${bi}-${li}`} style={{ borderBottom: "1px solid #eee", background: bi % 2 === 0 ? "#fff" : "#fafafa" }}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{b.bookingRef}</td>
                    <td style={tdStyle}>{b.customerName}</td>
                    <td style={{ ...tdStyle, textTransform: "capitalize" }}>{item.livestockType}</td>
                    <td style={{ ...tdStyle, textTransform: "capitalize" }}>{item.animalClass || "—"}</td>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{item.headCount}</td>
                    <td style={tdStyle}>{item.averageWeightKg || "—"}</td>
                    <td style={{ ...tdStyle, fontSize: 10 }}>{item.lastFed ? new Date(item.lastFed).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                    <td style={{ ...tdStyle, fontSize: 10 }}>{item.lastWatered ? new Date(item.lastWatered).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{item.fitForTransport ? <span style={{ color: "#007a3d", fontWeight: 700 }}>✓</span> : <span style={{ color: "#c00" }}>✗</span>}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── SIGNATURE & DECLARATION ── */}
        <div className="print-avoid-break">
          <div style={{ border: "1px solid #ddd", borderRadius: 4, padding: 20, marginBottom: 16 }}>
            <div style={{ ...sectionTitleStyle, borderBottom: "none", marginBottom: 16 }}>Approval &amp; Sign-Off</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24 }}>
              {[
                { label: "Master / Skipper", sub: "Name & Signature" },
                { label: "Stockman", sub: "Name & Signature" },
                { label: "Date & Time of Approval", sub: "DD/MM/YYYY HH:MM" },
              ].map(item => (
                <div key={item.label}>
                  <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#999", marginBottom: 8 }}>{item.label}</div>
                  <div style={{ borderBottom: "1px solid #1a1a1a", height: 36, marginBottom: 6 }} />
                  <div style={{ fontSize: 9, color: "#bbb" }}>{item.sub}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: "#fafafa", border: "1px solid #ddd", borderRadius: 4, padding: 16, fontSize: 10, color: "#555", lineHeight: 1.6 }}>
            <strong style={{ color: "#1a1a1a" }}>Declaration:</strong> I confirm that this load plan has been prepared in accordance with MNZ Rule Part 24c and the McCallum Bros Livestock Handling Procedures.
            All animals presented for loading have been verified as fit for transport by the Stockman. The stocking density does not exceed the maximum permissible floor area per head as specified in
            Table 4 of MNZ Rule Part 24c for voyages of less than 10 days. A hospital pen has been reserved equivalent to at least 1% of total pen floor space.
            All farmers have provided completed Livestock Booking &amp; Declaration Forms and veterinary certificates where required.
          </div>
        </div>

        {/* ── FOOTER ── */}
        <div style={{ marginTop: 24, paddingTop: 12, borderTop: "1px solid #eee", display: "flex", justifyContent: "space-between", fontSize: 9, color: "#bbb" }}>
          <span>McCallum Bros Ltd · Livestock Load Plan · {sailing.sailingRef}</span>
          <span>Generated {new Date().toLocaleString("en-NZ")} · Livestock Handling Procedures v1</span>
        </div>

      </div>
    </div>
  );
}

// ── VESSEL DIAGRAM SVG — visual guide, proportional pen widths from real
// areaM2, ramp shown as reserved space at its confirmed real position
// (between P5/P6), container slot correctly wider (reflects merged
// P12+P13 area) ──
function VesselDiagram({ portPens, starboardPens, loadingOrder = {} }) {
  const PEN_H = 64;
  const GAP = 6;
  const WALKWAY = 36;
  const MARGIN = 8;
  const LABEL_W = 20;
  const ROW_PX = 980;
  const MIN_PW = 34;
  const RAMP_AFTER_INDEX = 5;
  const RAMP_GAP_PX = 44;

  const layoutRow = (penList, insertRampGap) => {
    const totalArea = penList.reduce((s, p) => s + (Number(p.areaM2) || 1), 0) || 1;
    const rampReserve = insertRampGap ? RAMP_GAP_PX + GAP : 0;
    const usablePx = insertRampGap ? ROW_PX - rampReserve : ROW_PX;
    // usablePx is the space actually available for pen widths — GAP
    // between each pen (n-1 gaps for n pens) needs to come out of that
    // budget too, or the row's real total creeps past the target.
    const totalGaps = Math.max(0, penList.length - 1) * GAP;
    const widthBudget = Math.max(0, usablePx - totalGaps);

    // Pass 1: any pen whose pure proportional share would fall under
    // MIN_PW gets pinned to exactly MIN_PW.
    const areas = penList.map(p => Number(p.areaM2) || 1);
    const pinned = areas.map(a => (a / totalArea) * widthBudget < MIN_PW);
    const pinnedWidth = pinned.reduce((s, isPinned, i) => s + (isPinned ? MIN_PW : 0), 0);
    const remainingBudget = Math.max(0, widthBudget - pinnedWidth);
    const remainingArea = areas.reduce((s, a, i) => s + (pinned[i] ? 0 : a), 0) || 1;

    // Pass 2: redistribute whatever's left over proportionally among the
    // non-pinned pens — this is what guarantees the row's actual total
    // always equals widthBudget exactly, with no overflow from clamping.
    const widths = areas.map((a, i) => pinned[i] ? MIN_PW : (a / remainingArea) * remainingBudget);

    let acc = 0;
    const raw = penList.map((p, i) => {
      const w = widths[i];
      const startAcc = acc;
      acc += w + GAP;
      return { pen: p, w, startAcc };
    });
    const totalRawWidth = acc;

    let gapX = null;
    const base = LABEL_W + MARGIN;
    const items = raw.map((item, i) => {
      const pastGap = insertRampGap && i >= RAMP_AFTER_INDEX;
      if (pastGap && gapX === null) gapX = base + item.startAcc;
      return { pen: item.pen, x: base + item.startAcc + (pastGap ? rampReserve : 0), w: item.w };
    });
    if (insertRampGap && gapX === null) gapX = base + totalRawWidth;

    return { items, gapX };
  };
  const portResult = layoutRow(portPens, true);
  const starResult = layoutRow(starboardPens, false);
  const portLayout = portResult.items;
  const starLayout = starResult.items;
  const rowEnd = (layout) => layout.length ? layout[layout.length - 1].x + layout[layout.length - 1].w : LABEL_W + MARGIN;
  const rowWidth = Math.max(rowEnd(portLayout), rowEnd(starLayout)) - (LABEL_W + MARGIN);

  const totalW = LABEL_W + MARGIN + rowWidth + MARGIN;
  const totalH = MARGIN + PEN_H + WALKWAY + PEN_H + MARGIN + 32;

  // Same weight-aware capacity used throughout the app (SailingDetail.jsx):
  // single-class pens compare head count to head count (so a genuinely
  // full pen reads as exactly 100%, not ~97% — MPI max head is always
  // floored, so comparing raw area instead leaves a full pen's last
  // fractional sliver of space showing as "unused"). Mixed-class pens have
  // no single head-count ceiling, so area is the only meaningful
  // comparison there. Falls back to the pen's static maxHead only when it
  // has no area on record at all.
  function penCapacityPct(pen) {
    const allocList = pen?.allocations || [];
    const totalHead = allocList.reduce((s, a) => s + (a.headCount || 0), 0);
    const area = Number(pen?.areaM2 || 0);
    if (!area) return pen.maxHead > 0 ? totalHead / pen.maxHead : 0;
    const weights = [...new Set(allocList.map(a => Number(a.avgWeightKg || 0)).filter(Boolean))];
    if (weights.length === 1) {
      const mpiMax = Math.floor(area / getMpiMinArea(weights[0]));
      return mpiMax > 0 ? totalHead / mpiMax : 0;
    }
    const areaUsed = allocList.reduce((s, a) => s + Number(a.headCount || 0) * getMpiMinArea(Number(a.avgWeightKg || 0)), 0);
    return areaUsed / area;
  }

  function penColor(pen) {
    if (pen?.isHospitalPen) return "#fee2e2";
    const allocList = pen?.allocations || [];
    if (!allocList.length) return "#f8fafc";
    const pct = penCapacityPct(pen) * 100;
    if (pct >= 95) return "#fef2f2";
    if (pct >= 80) return "#fffbeb";
    return "#f0fdf4";
  }

  function penBorder(pen) {
    if (pen?.isHospitalPen) return "#fca5a5";
    const allocList = pen?.allocations || [];
    if (!allocList.length) return "#e2e8f0";
    const pct = penCapacityPct(pen) * 100;
    if (pct >= 95) return "#f87171";
    if (pct >= 80) return "#fbbf24";
    return "#86efac";
  }

  // A single "max head" number is only meaningful for a single-class pen —
  // a mixed pen has no one true head-count ceiling, so this returns null
  // and the tile shows the allocated count alone instead of a misleading
  // fraction against some other class's max.
  function penDisplayMax(pen) {
    const area = Number(pen?.areaM2 || 0);
    if (!area) return pen.maxHead;
    const weights = [...new Set((pen?.allocations || []).map(a => Number(a.avgWeightKg || 0)).filter(Boolean))];
    if (weights.length !== 1) return null;
    return Math.floor(area / getMpiMinArea(weights[0]));
  }

  const rampX = portResult.gapX;

  const renderPen = ({ pen, x, w }, y, i) => {
    const allocList = pen.allocations || [];
    const alloc = allocList[0];
    const totalAllocHead = allocList.reduce((s, a) => s + (a.headCount || 0), 0);
    const pct = alloc ? Math.round(penCapacityPct(pen) * 100) : 0;
    const displayMax = penDisplayMax(pen);
    if (pen.isContainerSlot) {
      return (
        <g key={i}>
          <rect x={x} y={y} width={w} height={PEN_H} fill="#eff6ff" stroke="#93c5fd" strokeWidth={1.5} rx={4} />
          <text x={x + w / 2} y={y + 14} textAnchor="middle" fontSize={9} fontWeight="700" fill="#475569">{pen.penNumber}</text>
          <text x={x + w / 2} y={y + 30} textAnchor="middle" fontSize={9} fontWeight="700" fill="#1d4ed8">20' CONTAINER</text>
          <text x={x + w / 2} y={y + 44} textAnchor="middle" fontSize={7.5} fill="#1e3a8a">{pen.maxTEU || 0} TEU · replaces P12+P13</text>
        </g>
      );
    }
    const name = alloc?.customerName || "";
    // Wrap long names across two lines instead of shrinking the font down
    // to near-illegible sizes on one line — a few extra pixels of tile
    // height reads far better than 6px text for something like "Chatham
    // Islands Land and Sea". Roughly scaled to the tile's actual width so
    // narrower pens still wrap sensibly.
    const maxLineChars = Math.max(Math.floor(w / 6), 8);
    const nameLines = (() => {
      if (name.length <= maxLineChars) return [name];
      const words = name.split(" ");
      let line1 = "";
      let idx = 0;
      while (idx < words.length && (line1 ? line1 + " " + words[idx] : words[idx]).length <= maxLineChars) {
        line1 = line1 ? `${line1} ${words[idx]}` : words[idx];
        idx++;
      }
      if (!line1) {
        line1 = words[0];
        idx = 1;
      }
      let line2 = words.slice(idx).join(" ");
      if (line2.length > maxLineChars) {
        line2 = line2.slice(0, Math.max(maxLineChars - 1, 3)) + "…";
      }
      return line2 ? [line1, line2] : [line1];
    })();
    const nameFontSize = nameLines.some(l => l.length > maxLineChars) ? 6.5 : nameLines.length > 1 ? 7 : 8.5;
    return (
      <g key={i}>
        <rect x={x} y={y} width={w} height={PEN_H} fill={penColor(pen)} stroke={penBorder(pen)} strokeWidth={1.5} rx={4} />
        <text x={x + w / 2} y={y + 14} textAnchor="middle" fontSize={9} fontWeight="700" fill="#475569">{pen.penNumber}</text>
        {pen.isHospitalPen ? (
          <text x={x + w / 2} y={y + 34} textAnchor="middle" fontSize={w < 45 ? 6 : w < 60 ? 7.5 : 9} fill="#dc2626" fontWeight="700">HOSPITAL</text>
        ) : alloc ? (
          <>
            <text x={x + w / 2} y={y + 30} textAnchor="middle" fontSize={9.5} fontWeight="700" fill="#1e293b">{displayMax != null ? `${totalAllocHead} / ${displayMax}` : `${totalAllocHead} head`}</text>
            {nameLines.map((line, li) => (
              <text key={li} x={x + w / 2} y={y + 41 + li * 8} textAnchor="middle" fontSize={nameFontSize} fill="#475569">{line}</text>
            ))}
            <text x={x + w / 2} y={y + 57} textAnchor="middle" fontSize={9} fontWeight="700" fill={pct >= 95 ? "#dc2626" : pct >= 80 ? "#d97706" : "#16a34a"}>{pct}%</text>
          </>
        ) : (
          <text x={x + w / 2} y={y + 36} textAnchor="middle" fontSize={8} fill="#cbd5e1">Empty</text>
        )}
      </g>
    );
  };

  return (
    <div style={{ width: "100%" }}>
      <svg viewBox={`0 0 ${totalW} ${totalH}`} preserveAspectRatio="xMidYMid meet" style={{ display: "block", width: "100%", height: "auto", maxHeight: 340, fontFamily: "Arial, sans-serif" }}>
        <rect x={LABEL_W} y={MARGIN / 2} width={totalW - LABEL_W - 4} height={totalH - MARGIN} rx={8} fill="#f8fafc" stroke="#cbd5e1" strokeWidth={1.5} />

        <text x={LABEL_W - 4} y={MARGIN + PEN_H / 2 + 4} textAnchor="end" fontSize={9} fontWeight="700" fill="#7a1428" transform={`rotate(-90, ${LABEL_W - 4}, ${MARGIN + PEN_H / 2 + 4})`}>PORT</text>
        <text x={LABEL_W - 4} y={MARGIN + PEN_H + WALKWAY + PEN_H / 2 + 4} textAnchor="end" fontSize={9} fontWeight="700" fill="#1a3a5c" transform={`rotate(-90, ${LABEL_W - 4}, ${MARGIN + PEN_H + WALKWAY + PEN_H / 2 + 4})`}>STARBOARD</text>

        <rect x={LABEL_W + MARGIN} y={MARGIN + PEN_H + 1} width={rowWidth} height={WALKWAY - 2} fill="#e2e8f0" stroke="#cbd5e1" strokeWidth={0.5} strokeDasharray="4,3" />
        <text x={LABEL_W + MARGIN + rowWidth / 2} y={MARGIN + PEN_H + WALKWAY / 2 + 3} textAnchor="middle" fontSize={9} fontWeight="700" fill="#334155">Raceway</text>

        <g>
          <rect x={rampX} y={MARGIN} width={RAMP_GAP_PX} height={PEN_H} fill="#cbd5e1" stroke="#94a3b8" strokeWidth={1.2} rx={3} />
          <text x={rampX + RAMP_GAP_PX / 2} y={MARGIN + PEN_H / 2 - 9} textAnchor="middle" fontSize={9} fill="#475569">↑</text>
          <text x={rampX + RAMP_GAP_PX / 2} y={MARGIN + PEN_H / 2 + 3} textAnchor="middle" fontSize={6.5} fontWeight="700" fill="#334155">RAMP</text>
          <text x={rampX + RAMP_GAP_PX / 2} y={MARGIN + PEN_H / 2 + 17} textAnchor="middle" fontSize={9} fill="#475569">↓</text>
        </g>

        {portLayout.map((item, i) => renderPen(item, MARGIN, i))}
        {starLayout.map((item, i) => renderPen(item, MARGIN + PEN_H + WALKWAY, i))}

        {[
          { color: "#f0fdf4", border: "#86efac", label: "Allocated <80%" },
          { color: "#fffbeb", border: "#fbbf24", label: "80–94%" },
          { color: "#fef2f2", border: "#f87171", label: "≥95%" },
          { color: "#fee2e2", border: "#fca5a5", label: "Hospital Pen" },
          { color: "#f8fafc", border: "#e2e8f0", label: "Empty" },
        ].map((item, i) => (
          <g key={i} transform={`translate(${LABEL_W + MARGIN + i * 110}, ${totalH - 22})`}>
            <rect width={10} height={10} fill={item.color} stroke={item.border} strokeWidth={1} rx={2} />
            <text x={14} y={9} fontSize={8} fill="#64748b">{item.label}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}